<script lang="ts">
	// Svelte
	import { createEventDispatcher } from 'svelte';
	// Stores
	import { devices } from 'src/stores/lists/devices';
import { rooms } from 'src/stores/lists/rooms';
import { temp_schemes } from 'src/stores/lists/scenes-derived';
	import { edit } from 'src/stores/ui/edit_lists';
	import { scenes } from 'src/stores/lists/scenes';
	import { get } from 'svelte/store';
	// Components
	import {
		ActionsContainer,
		Alert,
		Button,
		Container,
		Icon,
		Modal,
		Select,
		Text,
		toast,
	} from '@Shelly/shelly-design-system';
	// Types
	import type { Room } from 'src/types/Room';
	// Helpers
	import { format } from 'svelte-i18n';
	import {
		requestDeleteRoom,
		requestDeleteRoomThermostats,
		transferDevicesToRoom,
	} from '../../list-helpers';
	import { HiddenDevicesRoomID } from 'src/helpers/service_rooms_helpers';
	import { resetDrawer } from 'src/helpers/drawer_helpers';
	// Router
	import { push } from 'svelte-spa-router';

	export let showDeleteRoomModal = false;
	export let id: string;
	export let type;
	export let list;
	export let currentRoute;

	const dispatch = createEventDispatcher();

	let editRoom: any = {
		name: undefined,
		image: '',
	};
	let selectedTransferRoom = null;
	let roomNameValidationMsg = '';

	$: items = $edit && currentRoute === type ? items : [...list];
	$: otherRooms = Object.values($rooms)
		.filter((room: Room) => room.id !== editRoom.id)
		.map((room: Room) => ({ value: room.id, title: room.name }));

	const clearData = () => {
		editRoom = {
			name: undefined,
			image: '',
		};
		roomNameValidationMsg = '';
	};

	const roomHasDevices = (roomId) => {
		return Object.values($devices).filter((device) => device.room_id === roomId);
	};

	const onCloseDeleteModal = () => {
		showDeleteRoomModal = false;
		clearData();
	};

	const onDeleteRoom = async () => {
		// Re-home devices that live in this room BEFORE deleting it. The cloud
		// `interface/room/delete` endpoint does not reliably honor the `dest`
		// hint, so we explicitly bulk-update the affected devices (to the picked
		// transfer room, or to Hidden devices by default) and also patch the
		// local `devices` store so the UI updates immediately. See FE-1651.
		const transferRes = await transferDevicesToRoom(
			editRoom.id,
			selectedTransferRoom ?? HiddenDevicesRoomID,
		);

		if (!transferRes.isok) {
			return toast.open({
				type: 'danger',
				title: $format('text_failed_transfer', {
					default: 'Failed to transfer devices',
				}),
			});
		}

		const res = await requestDeleteRoom(editRoom.id, selectedTransferRoom);

		// When we are on edit mode items are not reactive
		items = items.filter((room) => room.id !== editRoom.id);

		if (res.isok) {
			let thermostatIds = [];
			Object.values($temp_schemes).forEach(
				(temp: { _meta: { [key: string]: any }; id: number }) => {
					if (temp._meta.room === editRoom.id) thermostatIds.push(temp.id.toString());
				},
			);
			if (thermostatIds.length > 0) {
				const res = await requestDeleteRoomThermostats(thermostatIds);
				if (res.isok) {
					thermostatIds.forEach((id) => {
						scenes.remove(+id);
						devices_status.remove(+id);
					});
					resetDrawer();
				} else {
					toast.open({
						type: 'danger',
						title: $format('alert_failed_delete_room_thermostats', {
							default: 'Failed to delete the room thermostats',
						}),
					});
				}
			}
			rooms.remove(editRoom.id);
			push(`/home/rooms/-1`);
			dispatch('delete', items);
		} else {
			return toast.open({
				type: 'danger',
				title:
					get(format)(`type_del_err_toast`, {
						values: { this_type: get(format)(`process_delete_room`) },
					}) || `Failed to delete room`,
			});
		}
		onCloseDeleteModal();
	};

	$: if (id !== '-1' && id !== undefined && showDeleteRoomModal && $rooms[id]) {
		editRoom = $rooms[id];
	}
</script>

<Modal
	on:close={onCloseDeleteModal}
	bind:showModal={showDeleteRoomModal}
	title={$format('prompt_title_delete_room', {
		default: 'Delete Room?',
	})}
>
	<Container slot="content" padding="0">
		<div class="d-flex flex-direction-column align-items-center">
			<Icon size="xxl" icon="fa-light fa-trash" class="text-danger mb-xs" />
			<Text class="text-bold text-center mb-s" styleType="controls-large">
				{editRoom.name}
			</Text>
			<Text class="mb-m width-full text-center">
				{$format('on_delete_item_msg', {
					values: {
						device_name: editRoom.name,
						type: $format('process_room', {
							default: 'room',
						}),
					},
					default: `Deleting ${$format('process_room', {
						default: 'room',
					}).toLowerCase()} ‘${editRoom.name}’ will permanently remove it from your account.`,
				})}
			</Text>
			{#if roomHasDevices(editRoom.id).length > 0}
				<ActionsContainer orientation="vertical" class="mb-m">
					<Text slot="first" class="mb-s width-full text-center">
						{$format('on_delete_room_has_devices_msg', {
							values: {
								room_name: editRoom.name,
								type: $format('process_room', {
									default: 'room',
								}),
							},
							default: `You have devices in ${$format('room_name', {
								default: editRoom.name,
							}).toLowerCase()}. Would you like to move them to another room?`,
						})}
					</Text>
					<Select
						aria-labelledby="select-38z2515"
						slot="second"
						icon={null}
						size="m"
						options={otherRooms}
						on
						label={$format('select_transfer_room', {
							default: 'Select another room to transfer devices',
						})}
						on:change={(e) => (selectedTransferRoom = e.detail.value)}
						id="select-38z2515"
					/>
					<Alert
						slot="third"
						class="width-full"
						type="warning"
						heading={$format('lists_after_deleting_room_msg', {
							default:
								'If no room is selected, all devices from this room will be moved to Hidden devices.',
						})}
					/>
				</ActionsContainer>
			{/if}
		</div>
	</Container>

	<div slot="footer" class="d-flex justify-content-end">
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
			responsiveSize={{
				sm: 'm',
			}}
			variant="secondary"
			on:click={onCloseDeleteModal}
		>
			{$format('button_cancel', { default: 'Cancel' })}
		</Button>
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			variant="danger"
			responsiveSize={{
				sm: 'm',
			}}
			on:click={onDeleteRoom}
		>
			{$format('button_delete_room', { default: 'Delete Room' })}
		</Button>
	</div>
</Modal>
